import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CreditCard, Sparkles } from "lucide-react";
import { fetchAssinaturaStatus } from "../api/assinaturaApi";
import { STATUS_LABELS, labelPlano, labelPeriodo, obterPlanoPago } from "../constants/planos";
import { sessionManager } from "../utils/sessionManager";
import VencimentoCountdown from "./VencimentoCountdown";
import dayjs from "dayjs";

const textoCountdownRenovacaoBanner = (dias) => {
  if (dias === 0) return "Hoje é o último dia para renovar.";
  if (dias === 1) return "Falta 1 dia para o vencimento.";
  return `Faltam ${dias} dias para o vencimento.`;
};

const SubscriptionBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const usuario = sessionManager.obterUsuario();
  const tipo = String(usuario?.tipo || "").toLowerCase();

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);

  const recarregar = useCallback(async () => {
    if (tipo === "super_admin") {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchAssinaturaStatus();
      setPayload(data);
    } catch {
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [tipo]);

  useEffect(() => {
    recarregar();
    const handler = () => { void recarregar(); };
    window.addEventListener("fz:assinatura:changed", handler);
    window.addEventListener("fz:estabelecimento:changed", handler);
    return () => {
      window.removeEventListener("fz:assinatura:changed", handler);
      window.removeEventListener("fz:estabelecimento:changed", handler);
    };
  }, [recarregar]);

  if (tipo === "super_admin" || loading || !payload || payload.isento) return null;
  if (location.pathname === "/assinatura") return null;

  const assinatura = payload.assinatura;
  const acesso = payload.acesso;
  const emCarencia = Boolean(assinatura?.carenciaAte || acesso?.motivo === "carencia");
  const status = emCarencia ? "carencia" : (assinatura?.status || "pending_plan");
  const dias = assinatura?.diasRestantes ?? 0;
  const renovacaoManual = Boolean(assinatura?.renovacaoManual);
  const alertaRenovacaoManual =
    status === "active" && renovacaoManual && dias <= 5 && dias >= 0;
  const ultimoDiaRenovacao =
    status === "active" && dias === 0 && Boolean(assinatura?.periodoFim) && renovacaoManual;
  const tone =
    status === "suspended" || status === "pending_plan" || status === "awaiting_payment" || status === "carencia" || ultimoDiaRenovacao || alertaRenovacaoManual
      ? "warning"
      : status === "trial"
        ? "trial"
        : "success";

  let titulo = STATUS_LABELS[status] || "Assinatura";
  let texto = "";

  if (status === "trial") {
    titulo = `Trial · ${dias} dia(s) restante(s)`;
    const nomePlano = obterPlanoPago(assinatura.plano)?.nome;
    texto = nomePlano
      ? `Escolha o plano ${nomePlano} (${labelPeriodo(assinatura.periodo)}) antes do fim do trial.`
      : "Escolha um plano pago em Plano e assinatura antes do fim do trial.";
  } else if (status === "active") {
    if (ultimoDiaRenovacao) {
      titulo = `Plano ${labelPlano(assinatura.plano)} · último dia`;
    } else {
      titulo = `Plano ${labelPlano(assinatura.plano)} · ${dias} dia(s) restante(s)`;
    }
    if (assinatura.renovacaoAutomatica) {
      texto = `Renovação automática no cartão em ${dayjs(assinatura.periodoFim).format("DD/MM/YYYY")}.`;
    } else if (ultimoDiaRenovacao) {
      texto = null;
    } else if (dias <= 5) {
      texto = `${textoCountdownRenovacaoBanner(dias)} Renove em Plano e assinatura.`;
    } else {
      texto = `Renove manualmente até ${dayjs(assinatura.periodoFim).format("DD/MM/YYYY")}.`;
    }
  } else if (status === "pending_plan") {
    titulo = "Escolha um plano";
    texto = "Seu estabelecimento precisa de um plano ativo para usar o painel e o app.";
  } else if (status === "awaiting_payment") {
    titulo = "Aguardando pagamento";
    texto = "Conclua o PIX ou boleto na tela de assinatura para liberar o acesso.";
  } else if (status === "carencia") {
    titulo = "Renove sua assinatura";
    texto = `Você tem até ${dayjs(assinatura.carenciaAte).format("DD/MM/YYYY HH:mm")} para renovar antes da suspensão.`;
  } else if (status === "suspended") {
    titulo = "Assinatura suspensa";
    texto = "Regularize o plano para restaurar o acesso completo ao painel e ao app.";
  }

  return (
    <div className="container subscription-banner-wrap">
      <details className={`fz-accordion fz-accordion--banner fz-accordion--tone-${tone}`}>
        <summary className="fz-accordion__summary">
          <span className="fz-accordion__summary-main">
            <span className="fz-accordion__summary-icon" aria-hidden="true">
              {status === "trial" ? <Sparkles size={18} /> : <CreditCard size={18} />}
            </span>
            <span className="fz-accordion__summary-text">{titulo}</span>
          </span>
          <span className="fz-accordion__summary-hint">Ver detalhes</span>
        </summary>
        <div className="fz-accordion__content">
          <div className={`subscription-banner subscription-banner--${tone}`}>
            <div className="subscription-banner__icon">
              {status === "trial" ? <Sparkles size={20} /> : <CreditCard size={20} />}
            </div>
            <div className="subscription-banner__copy">
              <strong>{titulo}</strong>
              {ultimoDiaRenovacao ? (
                <div className="subscription-banner__countdown-row">
                  <VencimentoCountdown alvo={assinatura.periodoFim} variant="banner" label="Renove em" />
                  <span>Hoje é o último dia. Renove em Plano e assinatura.</span>
                </div>
              ) : (
                <span>{texto}</span>
              )}
            </div>
            <button
              type="button"
              className="btn btn-outline btn-sm subscription-banner__action"
              onClick={() =>
                navigate("/assinatura", {
                  state: alertaRenovacaoManual || status === "carencia" ? { abrirRenovacao: true } : undefined,
                })
              }
            >
              {alertaRenovacaoManual || status === "carencia" ? "Renovar agora" : "Ver planos"}
            </button>
          </div>
        </div>
      </details>
    </div>
  );
};

export default SubscriptionBanner;
